import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, LogOut, Trash2, Loader2, Image as ImageIcon, Lock } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { LOGO } from "@/data/site";

const StaffLogin = ({ onLogin }) => {
  const { staffLogin } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try { await staffLogin(username, password); onLogin(); }
    catch (e2) { setErr("Invalid username or password"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <form onSubmit={submit} className="card-lux p-8 w-full max-w-sm" data-testid="staff-login-form">
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO} alt="Rossi Ceramics" className="h-14 w-14 object-contain rounded-lg bg-white p-1.5 mb-3" />
          <h1 className="font-serif text-2xl text-rossi-cream flex items-center gap-2"><Lock size={18} className="text-rossi-gold" /> Staff Portal</h1>
          <p className="text-xs text-rossi-stone mt-1">Upload job photos to the gallery</p>
        </div>
        <input placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream mb-3 focus:outline-none focus:border-rossi-gold/50" data-testid="staff-username" />
        <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream mb-3 focus:outline-none focus:border-rossi-gold/50" data-testid="staff-password" />
        {err && <p className="text-sm text-red-400 mb-3" data-testid="staff-error">{err}</p>}
        <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="staff-login-submit">{loading ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}</button>
      </form>
    </div>
  );
};

const Uploader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [caption, setCaption] = useState("");
  const [tag, setTag] = useState("");
  const [pair, setPair] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);

  const uploadLogo = async () => {
    if (!logoFile) { toast.error("Choose a logo image first."); return; }
    const fd = new FormData();
    fd.append("file", logoFile);
    setLogoBusy(true);
    try {
      await api.post("/site/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated across the site");
      setLogoFile(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Logo upload failed"); }
    finally { setLogoBusy(false); }
  };

  const loadItems = () => api.get("/gallery").then((r) => setItems(r.data || [])).catch(() => {});

  useEffect(() => {
    api.get("/gallery/categories").then((r) => { setCats(r.data); setCategory(r.data[0]); }).catch(() => {});
    loadItems();
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !category) { toast.error("Choose a category and an image."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("caption", caption);
    fd.append("tag", tag);
    fd.append("pair", pair);
    setUploading(true);
    try {
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to gallery");
      setFile(null); setCaption(""); setTag(""); setPair("");
      document.getElementById("staff-file-input").value = "";
      loadItems();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/gallery/${id}`); setItems((it) => it.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error("Could not remove"); }
  };

  return (
    <div className="container-rossi py-10">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Rossi Ceramics" className="h-9 w-9 object-contain rounded-md bg-white p-1" />
          <div>
            <h1 className="font-serif text-2xl text-rossi-cream">Staff Gallery Portal</h1>
            <p className="text-xs text-rossi-stone">Signed in as {user?.name}</p>
          </div>
        </div>
        <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost-gold !py-2" data-testid="staff-logout"><LogOut size={15} /> Sign out</button>
      </div>

      <div className="card-lux p-7 mb-10" data-testid="logo-upload-card">
        <h2 className="font-serif text-xl text-rossi-cream mb-4">Site logo</h2>
        <p className="text-sm text-rossi-cream/70 mb-4">Upload a logo to replace it across the whole site (navbar, footer, portals). JPEG, PNG or WEBP.</p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} className="text-sm text-rossi-cream/80 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-rossi-gold file:text-rossi-bg file:font-medium" data-testid="logo-file" />
          <button onClick={uploadLogo} disabled={logoBusy} className="btn-gold" data-testid="logo-upload-submit">{logoBusy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Update logo</button>
        </div>
      </div>

      <form onSubmit={upload} className="card-lux p-7 mb-10">
        <h2 className="font-serif text-xl text-rossi-cream mb-5 flex items-center gap-2"><Upload size={18} className="text-rossi-gold" /> Upload a job photo</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-rossi-cream/80 mb-2 block">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="staff-category">
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="text-sm text-rossi-cream/80 mb-2 mt-4 block">Caption (optional)</label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Marble en-suite in Bramhall" className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="staff-caption" />
            <label className="text-sm text-rossi-cream/80 mb-2 mt-4 block">Before / After (optional)</label>
            <div className="flex gap-2 mb-2">
              {[["", "None"], ["before", "Before"], ["after", "After"]].map(([v, l]) => (
                <button type="button" key={v || "none"} onClick={() => setTag(v)} className={`px-3 py-1.5 rounded-full text-sm ${tag === v ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80"}`} data-testid={`staff-tag-${v || "none"}`}>{l}</button>
              ))}
            </div>
            {tag && <input value={pair} onChange={(e) => setPair(e.target.value)} placeholder="Pair label — use the SAME on the matching Before & After" className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="staff-pair" />}
          </div>
          <div>
            <label className="text-sm text-rossi-cream/80 mb-2 block">Image (JPEG, PNG, WEBP)</label>
            <input id="staff-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm text-rossi-cream/80 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-rossi-gold file:text-rossi-bg file:font-medium" data-testid="staff-file" />
            {file && <p className="text-xs text-rossi-stone mt-2">{file.name}</p>}
          </div>
        </div>
        <button type="submit" disabled={uploading} className="btn-gold mt-5" data-testid="staff-upload-submit">{uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload to gallery</button>
      </form>

      <h2 className="font-serif text-xl text-rossi-cream mb-5 flex items-center gap-2"><ImageIcon size={18} className="text-rossi-gold" /> Uploaded photos ({items.length})</h2>
      {items.length === 0 ? (
        <p className="text-rossi-stone text-sm">No photos uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} className="card-lux overflow-hidden group relative" data-testid={`staff-item-${it.id}`}>
              <img src={`${API}/gallery/file/${it.id}`} alt={it.caption || it.category} className="w-full h-40 object-cover" />
              <div className="p-3">
                <p className="text-xs font-mono uppercase tracking-widest text-rossi-gold">{it.category}{it.tag ? ` · ${it.tag}` : ""}</p>
                {it.caption && <p className="text-xs text-rossi-cream/70 mt-1 truncate">{it.caption}</p>}
              </div>
              <button onClick={() => remove(it.id)} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-rossi-bg/80 text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`staff-delete-${it.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function StaffPortal() {
  const { user, loading } = useAuth();
  const [, force] = useState(0);
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-rossi-gold" /></div>;
  const isStaff = user && (user.role === "staff" || user.role === "admin");
  return (
    <div data-testid="staff-portal-page" className="pt-8">
      <Seo title="Staff Portal" description="Private staff portal for Rossi Ceramics." />
      {isStaff ? <Uploader /> : <StaffLogin onLogin={() => force((n) => n + 1)} />}
    </div>
  );
}
